
import { useCallback } from 'react';
import { 
  saveGameResultToSupabase, 
  saveUnlockedFactToSupabase 
} from '@/lib/supabase/game';
import type { GameData } from './types';

export const useGameDataActions = (
  gameData: GameData,
  updateGameData: (newData: Partial<GameData>) => Promise<void>,
  userData: any
) => {
  /**
   * Record a game result
   */
  const recordGameResult = useCallback(async (
    score: number, 
    moves: number, 
    timeInSeconds: number, 
    levelId?: number, 
    gridId?: number
  ) => {
    const isNewBest = score > gameData.bestScore;
    const today = new Date().toISOString().split('T')[0];
    
    console.log('🎮 Game Data - Recording game result:', {
      score, moves, timeInSeconds, levelId, gridId, isNewBest
    });

    const updates: Partial<GameData> = {
      bestScore: Math.max(score, gameData.bestScore),
      totalGamesPlayed: gameData.totalGamesPlayed + 1,
      lastPlayedDate: today,
      dailyChallengeCompleted: true
    };

    // Mark grid as completed if specified
    if (levelId && gridId) {
      const gridKey = `${levelId}-${gridId}`;
      updates.completedGrids = {
        ...gameData.completedGrids,
        [gridKey]: true
      };

      // Check if level should be unlocked
      const nextLevel = levelId + 1;
      if (nextLevel <= 9 && !gameData.unlockedLevels.includes(nextLevel)) {
        updates.unlockedLevels = [...gameData.unlockedLevels, nextLevel];
      }
    }

    await updateGameData(updates);

    // Save game result to Supabase
    if (userData?.email && levelId && gridId) {
      try {
        await saveGameResultToSupabase(
          userData.email, 
          score, 
          moves, 
          timeInSeconds, 
          levelId, 
          gridId, 
          isNewBest,
          undefined,
          userData
        );
      } catch (error) {
        console.error('🎮 Game Data - Error saving game result to Supabase:', error);
      }
    }

    return { isNewBest };
  }, [gameData, updateGameData, userData]);

  /**
   * Unlock a cultural fact
   */
  const unlockFact = useCallback(async (factKey: string) => {
    if (!gameData.unlockedFacts.includes(factKey)) {
      console.log('🎮 Game Data - Unlocking fact:', factKey);
      
      await updateGameData({
        unlockedFacts: [...gameData.unlockedFacts, factKey]
      });

      // Save to Supabase
      if (userData?.email) {
        try {
          await saveUnlockedFactToSupabase(userData.email, factKey, userData);
        } catch (error) {
          console.error('🎮 Game Data - Error saving unlocked fact to Supabase:', error);
        }
      }
    }
  }, [gameData.unlockedFacts, updateGameData, userData]);

  return {
    recordGameResult,
    unlockFact
  };
};
